import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  OutlinedInput,
  FormHelperText
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AppAutocompleteV2 from '@components/app-autocomplete/app-autocomplete-v2.tsx';
import { ScreenBadge } from '@utils/screen-badge.utils.tsx';
import { useErrorMessage } from '@hooks/use-error-message.ts';
import InfoTooltip from '@components/info-tooltip/info-tooltip.tsx';
import {
  trimFormValues,
  validateNotOnlySpaces
} from '@utils/trim-form-values-utils.ts';
import { CrudActionEnum } from '@models/global.model.ts';
import {
  convertToSelectedDepartment,
  unitsErrorMessages,
  unitsTypeList
} from '@modules/departments-and-units/utils/units.utils.ts';
import useUnitsStore from '@modules/departments-and-units/pages/units/store/units.store.ts';
import useDepartmentStore from '@modules/departments-and-units/pages/departments/store/departments.store.ts';
import DepartmentsDialogContent from '@modules/departments-and-units/pages/departments/components/departments-dialog-content/departments-dialog-content.tsx';
import AppDialog from '@shared/components/app-dialog/app-dialog';
import TimeRangePickerList from '@components/time-range-picker-list/time-range-picker-list.tsx';
import { TimeRange } from '@models/time-range-picker.model.ts';
import dayjs from 'dayjs';
import { UnitReqModel } from '@models/departments-and-units/units.model.ts';
import { FileTypeEnum } from '@models/media-library.model.ts';
import MediaLibraryComponent from '@components/media-library-component/media-library-component.tsx';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DepartmentReqModel } from '@models/departments-and-units/departments.model.ts';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';

const UnitsDialogContent: React.FC<{
  onSave: (data: UnitReqModel) => void;
  departments: { id: number; title: string; value: string }[];
  updateUnitData: any | null;
}> = ({ onSave, departments: initialDepartments, updateUnitData }) => {
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const { isLoading } = useUnitsStore();
  const { resetSelectedMedia } = useMediaLibraryStore();
  const { createDepartment, getDepartmentsList, departmentsList } =
    useDepartmentStore();

  const [openDepartmentDialog, setOpenDepartmentDialog] =
    useState<boolean>(false);
  const [updateDepartmentData, setUpdateDepartmentData] = useState<any>(null);
  const [departments, setDepartments] = useState(initialDepartments);

  const {
    register,
    formState: { errors, isValid },
    control,
    handleSubmit,
    reset,
    setValue,
    watch
  } = useForm<any>({ mode: 'onTouched' });

  const watchedType = watch('type');
  const watchedDepartment = watch('department');

  const computedTypeList = useMemo(() => {
    if (watchedType) {
      return Array.isArray(watchedType) ? watchedType : [watchedType];
    }
    return [];
  }, [watchedType]);

  const computedDepartmentList = useMemo(() => {
    if (watchedDepartment) {
      return Array.isArray(watchedDepartment)
        ? watchedDepartment
        : [watchedDepartment];
    }
    return [];
  }, [watchedDepartment]);
  const isTabletScreen = useMediaQuery('(max-width: 1715px)');
  const [timeRanges, setTimeRanges] = useState<TimeRange[]>([]);
  const [selectedMedia, setSelectedMedia] = React.useState<Record<string, any>>(
    {}
  );

  const departmentSelected = useMemo(() => {
    return convertToSelectedDepartment(updateUnitData);
  }, [updateUnitData]);

  const typeSelected = useMemo(() => {
    const selected = unitsTypeList.find(
      (item) => item.value === updateUnitData?.type
    );
    return selected ? [selected] : [];
  }, [updateUnitData]);

  useEffect(() => {
    if (updateUnitData) {
      reset({
        unitName: updateUnitData.name || '',
        department: departmentSelected[0] || null,
        description: updateUnitData.description || '',
        type: typeSelected[0] || null
      });

      if (updateUnitData.availableHours?.length) {
        const converted: TimeRange[] = updateUnitData.availableHours.map(
          (item: any) => ({
            start: dayjs(item.open, 'HH:mm'),
            end: dayjs(item.close, 'HH:mm')
          })
        );
        setTimeRanges(converted);
      }
    }
  }, [updateUnitData, reset, departmentSelected]);

  const handleTimeChange = (ranges: TimeRange[]) => {
    setTimeRanges(ranges);
  };

  const handleDepartmentSave = async (data: DepartmentReqModel) => {
    return new Promise(async (resolve) => {
      try {
        const res: any = await createDepartment({
          data,
          handleClose: () => {
            setOpenDepartmentDialog(false);
            setUpdateDepartmentData(null);
          }
        });

        const departmentData = res?.data || res;

        const newOption = {
          id: departmentData?.id || departmentData?._id,
          title: departmentData?.name || departmentData?.title,
          value: departmentData?.name || departmentData?.title
        };

        setDepartments((prev) => [...prev, newOption]);

        setValue('department', newOption, {
          shouldValidate: true,
          shouldDirty: true
        });

        await getDepartmentsList();

        resolve(newOption);
      } catch (error) {
        resolve(null);
      }
    });
  };

  const onSubmit = (data: UnitReqModel) => {
    const trimmedData = trimFormValues(data);

    const department = Array.isArray(data.department)
      ? data.department[0]
      : data.department;

    const type = Array.isArray(data.type) ? data.type[0] : data.type;

    const availableHours = timeRanges
      .filter(
        (range): range is { start: dayjs.Dayjs; end: dayjs.Dayjs } =>
          range.start !== null && range.end !== null
      )
      .map((range) => ({
        open: range.start.format('HH:mm'),
        close: range.end.format('HH:mm')
      }));

    const body: any = {
      name: trimmedData.unitName,
      description: trimmedData.description,
      image: selectedMedia.unitImage?.path || null,
      type: type.value,
      availableHours
    };

    if (department?.id) {
      body.departmentId = department?.id;
    }

    onSave(body);
  };

  useEffect(() => {
    if (!openDepartmentDialog) {
      setUpdateDepartmentData(null);
    }
  }, [openDepartmentDialog]);

  useEffect(() => {
    if (initialDepartments && initialDepartments.length > 0) {
      setDepartments(initialDepartments);
    }
  }, [initialDepartments]);

  useEffect(() => {
    if (!initialDepartments || initialDepartments.length === 0) {
      getDepartmentsList();
    }
  }, []);

  useEffect(() => {
    if (departmentsList && departmentsList.length > 0) {
      setDepartments((prevDepartments) => {
        const storeDepartments = departmentsList;
        const localDepartments = prevDepartments.filter(
          (dept) =>
            !storeDepartments.some((storeDept) => storeDept.id === dept.id)
        );
        return [...storeDepartments, ...localDepartments];
      });
    }
  }, [departmentsList]);

  useEffect(() => {
    return () => {
      resetSelectedMedia('unitImage');
    };
  }, []);

  return (
    <div>
      <div className="mt-4 flex gap-14 max-lg-xl:gap-5">
        <div className="media-wrapper">
          <div className="flex mb-1.5">{t('label_image')}</div>

          <MediaLibraryComponent
            mediaKey={'unitImage'}
            width={isTabletScreen ? 140 : 174}
            height={isTabletScreen ? 140 : 174}
            allowedTypes={[FileTypeEnum.Image]}
            path={updateUnitData?.image}
            onSelect={(media) =>
              setSelectedMedia((prev) => ({ ...prev, unitImage: media }))
            }
          />
        </div>

        <div className="w-full">
          <form className="flex flex-col gap-1">
            <FormControl error={!!errors.unitName}>
              <FormLabel className="flex">
                <span className="reuqired-symbole">*</span>
                {t('label_unit_name')}
                <InfoTooltip text="tooltip_info_name" />
              </FormLabel>
              <OutlinedInput
                type="text"
                {...register('unitName', {
                  required: true,
                  validate: { notOnlySpaces: validateNotOnlySpaces }
                })}
                placeholder={t('placeholder_unit_name')}
              />
              <FormHelperText>
                {errors?.unitName &&
                  getErrorMessage({
                    errors,
                    fieldName: 'unitName',
                    errorMessages: unitsErrorMessages
                  })}
              </FormHelperText>
            </FormControl>

            <AppAutocompleteV2
              key={departments.length}
              name="department"
              control={control}
              selectedList={computedDepartmentList}
              options={departments}
              label={
                <span className="flex items-center">
                  {t('label_department')}
                  <InfoTooltip text="tooltip_info_department" />
                </span>
              }
              placeholder={t('placeholder_department')}
              withChip={false}
              addItemConfig={{
                action: CrudActionEnum.AddWithDialog,
                callback: (data: any) => {
                  setOpenDepartmentDialog(true);
                  setUpdateDepartmentData({ name: data.title });
                }
              }}
            />

            <AppAutocompleteV2
              name="type"
              control={control}
              selectedList={computedTypeList}
              label={
                <span className="flex items-center gap-2 mt-4">
                  <span className="flex items-center">
                    <span className="reuqired-symbole">*</span>
                    {t('label_type')}
                    <InfoTooltip text="tooltip_info_type" />
                  </span>
                  {watchedType && <ScreenBadge type={watchedType?.value} />}
                </span>
              }
              placeholder={t('placeholder_type')}
              options={unitsTypeList}
              withChip={false}
              validationProps={{
                errors,
                errorMessages: unitsErrorMessages,
                getErrorMessage,
                validationRules: { required: true }
              }}
              renderOptionBadge={(option) => (
                <ScreenBadge type={option?.value} />
              )}
            />

            <div className="w-full max-w-[600px] max-lg-xl:max-w-[380px]">
              <TimeRangePickerList
                updateData={timeRanges}
                onChange={handleTimeChange}
                mode="multiple"
                required={true}
                helperText={t('validation_working_time_required')}
              />
            </div>

            <FormControl error={!!errors.description}>
              <FormLabel className="flex">
                <span className="reuqired-symbole">*</span>
                {t('label_description')}
                <InfoTooltip text="tooltip_info_description" />
              </FormLabel>
              <OutlinedInput
                multiline
                rows={5}
                {...register('description', {
                  required: true,
                  validate: { notOnlySpaces: validateNotOnlySpaces }
                })}
                placeholder={t('placeholder_description')}
              />
              <FormHelperText>
                {errors?.description &&
                  getErrorMessage({
                    errors,
                    fieldName: 'description',
                    errorMessages: unitsErrorMessages
                  })}
              </FormHelperText>
            </FormControl>
          </form>

          <div className="flex justify-end pt-6 pb-1 sticky bottom-0 bg-white">
            <Button
              variant="contained"
              loading={isLoading()}
              disabled={
                !isValid ||
                !timeRanges.some((r) => r.start !== null && r.end !== null)
              }
              onClick={handleSubmit(onSubmit)}
            >
              {t('save')}
            </Button>
          </div>
        </div>
      </div>

      <AppDialog
        open={openDepartmentDialog}
        size={isTabletScreen ? 'sm' : 'md'}
        title={t('department_dialog_title')}
        content={
          <DepartmentsDialogContent
            assignedAdminList={[]}
            updateDepartmentData={updateDepartmentData}
            onSave={handleDepartmentSave}
          />
        }
        onClose={() => setOpenDepartmentDialog(false)}
      />
    </div>
  );
};

export default UnitsDialogContent;
